import { FC, Fragment, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TransactionType,
  TransactionTypesOptions,
  withStyles,
  Card,
  Box,
  formatAmount,
  removeWhiteSpaceURI,
  TransactionStatusStepper,
} from 'vopay';
import { Title, UtilButton } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransactionHistory from '@/hooks/useApi/useTransactionHistory';
import styles from './TransactionCard.module.scss';

type TransactionCardProps = {
  receiverName: string;
  amount: string;
  currency: string;
  transactionId: string;
  parentTransactionId?: string;
  type: TransactionTypesOptions;
  parentType?: TransactionTypesOptions;
  subTransactionId?: string;
  subTransactionType?: TransactionTypesOptions;
  failureReason: string;
  senderName?: string;
  contactId?: string;
  jwt?: string;
  isMainAccount: boolean;
  extraInformation?: ReactNode;
  additionalId?: ReactNode;
};

const cn = withStyles(styles);

const TransactionCard: FC<TransactionCardProps> = ({
  receiverName,
  amount,
  currency,
  transactionId,
  parentTransactionId,
  type,
  parentType = 'All',
  subTransactionId,
  subTransactionType = 'All',
  failureReason,
  senderName,
  contactId,
  jwt,
  isMainAccount,
  extraInformation,
  additionalId,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Card',
  });

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const toast = useToast();
  const { transactionHistory, fetchTransactionHistory } = useTransactionHistory({ bearer: jwt });

  // 'Wire' is a send-only rail whose type name carries no direction
  const isWithdrawal = type === TransactionType.WIRE || type.toLowerCase().includes('withdrawal');
  const isFunding = type.toLowerCase().includes('funding');

  type IdEntry = { label: string; id: string; type: TransactionTypesOptions; clickable: boolean };

  const transactionIdLabel = t('transactionId', { defaultValue: 'Transaction ID' });
  const parentIdLabel = t('parentId', { defaultValue: 'Parent ID' });

  const idEntries: IdEntry[] =
    subTransactionId && subTransactionType
      ? [
          { label: parentIdLabel, id: transactionId, type, clickable: false },
          { label: transactionIdLabel, id: subTransactionId, type: subTransactionType, clickable: true },
        ]
      : parentTransactionId && parentType
        ? [
            { label: parentIdLabel, id: parentTransactionId, type: parentType, clickable: true },
            { label: transactionIdLabel, id: transactionId, type, clickable: false },
          ]
        : [{ label: transactionIdLabel, id: transactionId, type, clickable: false }];

  useEffect(() => {
    if (!transactionId) return;

    (async () => {
      try {
        await fetchTransactionHistory({
          TransactionID: transactionId,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch transaction history' }));
      }
    })();
  }, [transactionId]);

  return (
    <Card className={cn('card')}>
      <Box jc="sb" gapSize="24px">
        <Box flx="col" gapSize="24px">
          <Box flx="col" gapSize="9px">
            <Title level="2">
              {type === 'Account Transfer' && senderName && !extraInformation ? (
                <>
                  {senderName} <span className={cn('grey')}>{t('to', { defaultValue: 'to' })}</span> {receiverName}
                </>
              ) : contactId && isMainAccount ? (
                <UtilButton
                  className={cn('name')}
                  onClick={() => {
                    if (clientAccountsEnabled) {
                      window.open(`/client-accounts/${contactId}`, '_blank');
                    } else {
                      window.open(`/contacts/${contactId}`, '_blank');
                    }
                  }}
                >
                  {receiverName}
                </UtilButton>
              ) : (
                receiverName
              )}
            </Title>

            {extraInformation}
          </Box>

          <Box flx="col" gapSize="32px" padding="0 0 0 6px">
            <Box flx="col" gapSize="5px">
              <Title level="5" emphasized={false} className={cn('grey')}>
                {isWithdrawal
                  ? t('amountSent', { defaultValue: 'Amount Sent' })
                  : isFunding
                    ? t('amountRequested', { defaultValue: 'Amount Requested' })
                    : t('amount', { defaultValue: 'Amount' })}
              </Title>

              <Title level="4">
                {currency} {formatAmount(amount)}
              </Title>
            </Box>

            <div className={cn('idContainer')}>
              {idEntries.map((entry, index) => {
                const idBlock = (
                  <Box flx="col" gapSize="6px">
                    <div className={cn('grey', 'row')}>{entry.label}</div>

                    {entry.clickable ? (
                      <UtilButton
                        className={cn('idButton')}
                        onClick={() => {
                          window.open(
                            `/transactions-history/details/${removeWhiteSpaceURI(entry.type)}/${entry.id}/${currency}`,
                            '_blank',
                          );
                        }}
                      >
                        {entry.id}
                      </UtilButton>
                    ) : (
                      <div className={cn('idContainer')}>{entry.id}</div>
                    )}
                  </Box>
                );

                return index === 0 ? (
                  <Fragment key={`${entry.label}-${entry.id}`}>{idBlock}</Fragment>
                ) : (
                  <div key={`${entry.label}-${entry.id}`} className={cn('second')}>
                    {idBlock}
                  </div>
                );
              })}

              {additionalId && <div className={cn('second')}>{additionalId}</div>}
            </div>
          </Box>
        </Box>

        <TransactionStatusStepper transactionHistory={transactionHistory} failureReason={failureReason} />
      </Box>
    </Card>
  );
};

export default TransactionCard;
